import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { Box, Button, Checkbox, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import {
  CreateGroupInput,
  GroupInputList,
  useBatchCreateGroupsMutation,
  useUpsertBillingCustomerMutation,
} from 'api';
import { Layouts, Loaders, PaddedPaper, ValidatedTextFieldNext } from 'components';
import { useCurrentUser, useLocalStorage } from 'hooks';
import { getAppImage } from 'helpers';
import * as paths from 'paths';
import { useTrackingQuery } from 'app-react-query/hooks';
import { FunnelEvent } from 'analytics';

import { getCreateGroupInitialValues } from 'features/groups/helpers';
import { useBatchCreateGroups } from 'features/groups/updaters';

const useStyles = makeStyles(theme => ({
  root: {
    paddingLeft: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  checked: {},
  disabled: {},
  colorPrimary: {
    '&$checked': {
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    '&$disabled': {
      color: theme.palette.primary.main,
      opacity: 0.5,
    },
  },
}));

const SetupWorkspaceGroups: FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();

  const track = useTrackingQuery();
  const [newUserSignUpWorkflow] = useLocalStorage('newUserSignUp', false);
  const currentUser = useCurrentUser();
  const batchCreateGroupsOptions = useBatchCreateGroups();

  const [selectedIndexes, setSelectedIndexes] = useState({
    0: true,
    1: true,
    2: true,
    3: true,
    4: true,
  });

  const groupsToCreate: GroupInputList = {
    groups: [
      { ...getCreateGroupInitialValues(), title: t('onboarding:clientGroupsAnnouncements') },
      { ...getCreateGroupInitialValues(), title: t('onboarding:clientGroupsSales') },
      { ...getCreateGroupInitialValues(), title: t('onboarding:clientGroupsCompetition') },
      { ...getCreateGroupInitialValues(), title: t('onboarding:clientGroupsRandom') },
      { ...getCreateGroupInitialValues(), title: t('onboarding:clientGroupsGeneral') },
    ],
  };

  const { mutate, isLoading } = useBatchCreateGroupsMutation({
    onSuccess: () => {
      track(
        newUserSignUpWorkflow ? FunnelEvent.SLNUCCCMCG : FunnelEvent.SLEUCCCMCG,
        currentUser.id,
        {
          client_groups: groupsToCreate?.groups?.length,
        }
      );
      history.replace(paths.setupWorkspaceInvites);
    },
    ...batchCreateGroupsOptions,
  });

  const { mutate: upsertBillingCustomer } = useUpsertBillingCustomerMutation();

  useEffect(() => upsertBillingCustomer({}), []);

  function handleSubmit(values: GroupInputList) {
    const groupsToSend: CreateGroupInput[] = [];

    Object.keys(selectedIndexes).forEach((value, index) => {
      const isEnabled = selectedIndexes[index];
      const isValid = values.groups[index].title.length > 0;

      if (isEnabled && isValid) {
        groupsToSend.push(values.groups[index]);
      }
    });

    mutate({ group_input_list: { groups: groupsToSend } });
  }

  function handleCheckboxToggle(event: ChangeEvent<HTMLInputElement>, index: number) {
    setSelectedIndexes({
      ...selectedIndexes,
      [index]: event.target.checked,
    });
  }

  function renderImage() {
    return <img width={500} height={500} {...getAppImage('illustrations/browser.png')} />;
  }

  function renderCreateGroup(_: CreateGroupInput, index: number) {
    const isLast = index === groupsToCreate.groups.length - 1;

    return (
      <Box key={index} paddingTop={2}>
        <Grid container spacing={2}>
          <Grid item>
            <Checkbox
              classes={classes}
              onChange={event => handleCheckboxToggle(event, index)}
              checked={selectedIndexes[index]}
              disabled={isLast}
            />
          </Grid>
          <Grid item xs>
            <ValidatedTextFieldNext
              fullWidth
              disabled={isLast}
              name={`groups[${index}].title`}
              placeholder={t('groups:enterGroupName')}
            />
          </Grid>
        </Grid>
      </Box>
    );
  }

  function renderMain() {
    return (
      <PaddedPaper disableBorder padding="64px 48px">
        <Formik onSubmit={handleSubmit} initialValues={groupsToCreate}>
          {({ submitForm }) => (
            <Layouts.OnboardingBox component={Form}>
              <Typography align="center" paragraph variant="h2">
                {t('onboarding:clientGroupsHeader')}
              </Typography>
              <Typography align="center" paragraph color="textSecondary">
                {t('onboarding:clientGroupsSubHead')}
              </Typography>
              {groupsToCreate.groups.map(renderCreateGroup)}
              <Box paddingTop={3}>
                <Button
                  fullWidth
                  onClick={submitForm}
                  size="large"
                  variant="contained"
                  color="primary"
                  disabled={isLoading}
                >
                  {isLoading ? <Loaders.Standard height={26} /> : t('common:next')}
                </Button>
              </Box>
            </Layouts.OnboardingBox>
          )}
        </Formik>
      </PaddedPaper>
    );
  }

  return <Layouts.Onboarding image={renderImage()} main={renderMain()} />;
};

export default SetupWorkspaceGroups;
